import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import './index.css';

import { useAuthStore } from './store';
import AppShell from './components/layout/AppShell';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import POSPage from './pages/POSPage';
import InventoryPage from './pages/InventoryPage';
import SalesPage from './pages/SalesPage';
import EmployeesPage from './pages/EmployeesPage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import AlertsPage from './pages/AlertsPage';

const qc = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, staleTime: 30_000, refetchOnWindowFocus: true },
  },
});

function RequireAuth({ children }) {
  const { isAuthenticated } = useAuthStore();
  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

function RequireRole({ roles, children }) {
  const { user } = useAuthStore();
  if (!user || !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <QueryClientProvider client={qc}>
      <BrowserRouter>
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: { fontFamily: 'Sora, sans-serif', fontSize: '13px', borderRadius: '10px' },
            success: { iconTheme: { primary: '#0d9e6e', secondary: '#fff' } },
            error:   { iconTheme: { primary: '#dc2626', secondary: '#fff' } },
          }}
        />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/" element={<RequireAuth><AppShell /></RequireAuth>}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="pos"       element={<POSPage />} />
            <Route path="inventory" element={<InventoryPage />} />
            <Route path="sales"     element={<SalesPage />} />
            <Route path="alerts"    element={<AlertsPage />} />
            <Route path="employees" element={
              <RequireRole roles={['admin','supervisor']}><EmployeesPage /></RequireRole>
            } />
            <Route path="reports"   element={
              <RequireRole roles={['admin','supervisor']}><ReportsPage /></RequireRole>
            } />
            <Route path="settings"  element={
              <RequireRole roles={['admin']}><SettingsPage /></RequireRole>
            } />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
